import { useState, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Alert, AlertDescription } from "../components/ui/alert";
import AdminSidebar from "../components/layout/AdminSidebar";
import {
  Upload,
  Map,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  ArrowRight,
  Download,
  RefreshCw,
  MapPin,
  Home,
  AlertCircle,
  Trash2,
  Filter,
  ListFilter,
} from "lucide-react";

const BulkGISImportPage = () => {
  const { token } = useAuth();
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Parsed data state
  const [fileData, setFileData] = useState(null);
  const [features, setFeatures] = useState([]);
  
  // Selection state
  const [selectedMatches, setSelectedMatches] = useState(new Set());
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  
  // New import options
  const [importMode, setImportMode] = useState("selected"); // "all" or "selected"
  const [replaceAllBoundaries, setReplaceAllBoundaries] = useState(false);
  const [filterList, setFilterList] = useState(""); // Comma-separated list of names/lot numbers
  
  // Apply state
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  
  // Confirmation modal
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file extension
    const validExtensions = ['.kml', '.kmz', '.geojson', '.json'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(ext)) {
      toast.error("Invalid file type. Please upload KML, KMZ, or GeoJSON file.");
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    setFileData(null);
    setFeatures([]);
    setSelectedMatches(new Set());
    setApplyResult(null);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(
        `${API}/gis/bulk-import/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          },
        }
      );
      
      setFileData(response.data);
      setFeatures(response.data.features || []);
      
      // Auto-select all matched features
      const matched = new Set(
        response.data.features
          .filter(f => f.matched && f.property_id)
          .map(f => f.property_id)
      );
      setSelectedMatches(matched);
      
      toast.success(`File parsed: ${response.data.total_features} features found, ${response.data.matched_features} matched`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  // Toggle selection
  const toggleSelection = (propertyId) => {
    const newSelection = new Set(selectedMatches);
    if (newSelection.has(propertyId)) {
      newSelection.delete(propertyId);
    } else {
      newSelection.add(propertyId);
    }
    setSelectedMatches(newSelection);
  };

  // Select all matched
  const selectAllMatched = () => {
    const matched = new Set(
      features
        .filter(f => f.matched && f.property_id)
        .map(f => f.property_id)
    );
    setSelectedMatches(matched);
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedMatches(new Set());
  };

  // Apply boundaries
  const handleApply = async () => {
    setConfirmModalOpen(false);
    setApplying(true);
    
    // Parse filter list if provided
    const filterListArray = filterList
      .split(/[,\n]/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
    
    try {
      const response = await axios.post(
        `${API}/gis/bulk-import/apply`,
        {
          file_id: fileData.file_id,
          selected_matches: Array.from(selectedMatches),
          overwrite_existing: overwriteExisting,
          replace_all_boundaries: replaceAllBoundaries,
          import_mode: importMode,
          filter_list: filterListArray.length > 0 ? filterListArray : null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setApplyResult(response.data);
      toast.success(`Applied boundaries to ${response.data.applied} properties`);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply boundaries");
    } finally {
      setApplying(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFileData(null);
    setFeatures([]);
    setSelectedMatches(new Set());
    setApplyResult(null);
    setOverwriteExisting(false);
    setReplaceAllBoundaries(false);
    setImportMode("selected");
    setFilterList("");
  };

  const matchedCount = features.filter(f => f.matched).length;
  const unmatchedCount = features.length - matchedCount;

  return (
    <AdminSidebar>
    <div className="min-h-screen bg-background" data-testid="bulk-gis-import-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-3">
            <Map className="w-6 h-6 text-primary" />
            <div>
              <h1 className="font-serif text-xl font-semibold">Bulk GIS Import</h1>
              <p className="text-sm text-muted-foreground">
                Upload KML, KMZ, or GeoJSON to import lot boundaries
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Step 1: Upload */}
        {!fileData && !applyResult && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Step 1: Upload GIS File
              </CardTitle>
              <CardDescription>
                Upload a KML, KMZ, or GeoJSON file containing lot boundaries.
                The system will automatically match boundaries to existing properties.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                {uploading ? (
                  <div className="space-y-4">
                    <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Uploading and parsing file...</p>
                    <Progress value={uploadProgress} className="max-w-xs mx-auto" />
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-sm text-muted-foreground mb-4">
                      Drag and drop a file here, or click to browse
                    </p>
                    <input
                      type="file"
                      accept=".kml,.kmz,.geojson,.json"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="gis-file-upload"
                    />
                    <label htmlFor="gis-file-upload">
                      <Button asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Select File
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-muted-foreground mt-4">
                      Supported formats: KML, KMZ, GeoJSON (max 100 MB)
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Review Matches */}
        {fileData && !applyResult && (
          <div className="space-y-6">
            {/* Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Step 2: Review Matches
                </CardTitle>
                <CardDescription>
                  Review the parsed features and select which boundaries to apply.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{fileData.total_features}</p>
                    <p className="text-xs text-muted-foreground">Total Features</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{matchedCount}</p>
                    <p className="text-xs text-muted-foreground">Matched</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{unmatchedCount}</p>
                    <p className="text-xs text-muted-foreground">Unmatched</p>
                  </div>
                </div>

                {/* Import Options */}
                <div className="border rounded-lg p-4 mb-6 bg-muted/30">
                  <h4 className="font-medium mb-4 flex items-center gap-2">
                    <ListFilter className="w-4 h-4" />
                    Import Options
                  </h4>
                  
                  {/* Import Mode */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Import Mode</Label>
                      <RadioGroup 
                        value={importMode} 
                        onValueChange={setImportMode}
                        className="flex gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="selected" id="mode-selected" />
                          <Label htmlFor="mode-selected" className="text-sm cursor-pointer">
                            Import Selected Only
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="all" id="mode-all" />
                          <Label htmlFor="mode-all" className="text-sm cursor-pointer">
                            Import All Matched
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Filter List - Only show when import mode is "all" */}
                    {importMode === "all" && (
                      <div>
                        <Label className="text-sm font-medium mb-2 block">
                          <Filter className="w-4 h-4 inline mr-1" />
                          Filter by List (Optional)
                        </Label>
                        <Textarea
                          placeholder="Enter lot numbers or feature names to filter import (one per line or comma-separated)&#10;Example:&#10;LOT 1&#10;LOT 2&#10;194335101003"
                          value={filterList}
                          onChange={(e) => setFilterList(e.target.value)}
                          rows={4}
                          className="font-mono text-sm"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Leave empty to import all {matchedCount} matched boundaries
                        </p>
                      </div>
                    )}

                    {/* Replace All Boundaries */}
                    <div className="pt-2 border-t">
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id="replace-all"
                          checked={replaceAllBoundaries}
                          onCheckedChange={setReplaceAllBoundaries}
                        />
                        <div>
                          <Label htmlFor="replace-all" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                            <Trash2 className="w-4 h-4 text-destructive" />
                            Replace Previous Import
                          </Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Clear ALL existing property boundaries before importing new ones
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Warning for Replace */}
                    {replaceAllBoundaries && (
                      <Alert variant="destructive" className="mt-2">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          This will delete ALL existing boundary data from your properties before importing. This action cannot be undone.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{selectedMatches.size} selected</Badge>
                    {importMode === "selected" && (
                      <>
                        <Button variant="ghost" size="sm" onClick={selectAllMatched}>
                          Select All Matched
                        </Button>
                        <Button variant="ghost" size="sm" onClick={deselectAll}>
                          Deselect All
                        </Button>
                      </>
                    )}
                    {importMode === "all" && (
                      <span className="text-sm text-muted-foreground">
                        Will import {filterList.trim() ? "filtered" : "all"} {matchedCount} matched boundaries
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="overwrite"
                      checked={overwriteExisting}
                      onCheckedChange={setOverwriteExisting}
                    />
                    <Label htmlFor="overwrite" className="text-sm">
                      Overwrite existing boundaries
                    </Label>
                  </div>
                </div>

                {/* Features List */}
                <div className="border rounded-lg max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="p-2 text-left w-10"></th>
                        <th className="p-2 text-left">Feature Name</th>
                        <th className="p-2 text-left">Type</th>
                        <th className="p-2 text-left">Match</th>
                        <th className="p-2 text-left">Property</th>
                      </tr>
                    </thead>
                    <tbody>
                      {features.map((feature, index) => (
                        <tr 
                          key={index} 
                          className={`border-t ${feature.matched ? 'hover:bg-green-50' : 'bg-muted/30'}`}
                        >
                          <td className="p-2">
                            {feature.matched && (
                              <Checkbox
                                checked={selectedMatches.has(feature.property_id)}
                                onCheckedChange={() => toggleSelection(feature.property_id)}
                              />
                            )}
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-2">
                              {feature.geometry_type === "Polygon" ? (
                                <MapPin className="w-4 h-4 text-green-600" />
                              ) : (
                                <MapPin className="w-4 h-4 text-gray-400" />
                              )}
                              <span className="truncate max-w-[150px]">
                                {feature.name || `Feature ${index + 1}`}
                              </span>
                            </div>
                          </td>
                          <td className="p-2">
                            <Badge variant="outline" className="text-xs">
                              {feature.geometry_type}
                            </Badge>
                          </td>
                          <td className="p-2">
                            {feature.matched ? (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                {feature.match_type} ({feature.match_confidence}%)
                              </Badge>
                            ) : (
                              <Badge variant="secondary">
                                <XCircle className="w-3 h-3 mr-1" />
                                No match
                              </Badge>
                            )}
                          </td>
                          <td className="p-2">
                            {feature.matched ? (
                              <div>
                                <p className="font-medium">Lot {feature.property_lot_number}</p>
                                <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                  {feature.property_address}
                                </p>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-between">
              <Button variant="outline" onClick={handleReset}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Start Over
              </Button>
              <Button
                onClick={() => setConfirmModalOpen(true)}
                disabled={(importMode === "selected" && selectedMatches.size === 0) || applying}
              >
                {applying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    {importMode === "all" 
                      ? `Import ${filterList.trim() ? "Filtered" : "All"} ${matchedCount} Boundaries`
                      : `Apply ${selectedMatches.size} Boundaries`
                    }
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {applyResult && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Import Complete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{applyResult.applied}</p>
                  <p className="text-xs text-muted-foreground">Applied</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{applyResult.skipped}</p>
                  <p className="text-xs text-muted-foreground">Skipped</p>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-2xl font-bold">{applyResult.total_processed}</p>
                  <p className="text-xs text-muted-foreground">Total Processed</p>
                </div>
              </div>

              {applyResult.errors.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-red-600 mb-2">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    {applyResult.errors.length} errors occurred:
                  </p>
                  <ul className="text-sm text-muted-foreground list-disc list-inside">
                    {applyResult.errors.map((err, i) => (
                      <li key={i}>{err.property_id}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-4">
                <Button onClick={handleReset}>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Another File
                </Button>
                <Button variant="outline" asChild>
                  <a href="/board/gis">
                    <Map className="w-4 h-4 mr-2" />
                    View GIS Map
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Confirmation Modal */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Import</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3">
                <p>
                  {importMode === "all" 
                    ? `You are about to import ${filterList.trim() ? "filtered" : "all"} ${matchedCount} matched boundaries.`
                    : `You are about to apply GIS boundaries to ${selectedMatches.size} properties.`
                  }
                </p>
                
                {replaceAllBoundaries && (
                  <div className="p-3 bg-destructive/10 text-destructive rounded-lg border border-destructive/20">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    <strong>Warning:</strong> ALL existing property boundaries will be deleted before import.
                  </div>
                )}
                
                {overwriteExisting && !replaceAllBoundaries && (
                  <div className="text-amber-600">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Existing boundaries on selected properties will be overwritten.
                  </div>
                )}
                
                {filterList.trim() && importMode === "all" && (
                  <div className="text-sm text-muted-foreground">
                    Filter list: {filterList.split(/[,\n]/).filter(s => s.trim()).length} items
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApply}
              variant={replaceAllBoundaries ? "destructive" : "default"}
            >
              {replaceAllBoundaries ? "Delete & Import" : "Apply Boundaries"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AdminSidebar>
  );
};

export default BulkGISImportPage;
